import { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { EmptyState } from '@carboledger/ui';
import { ROUTES, MODULE_CERTIFICATE_TYPES } from '@/app/routes';
import { DetailPageHeader } from '@/components/common';
import { LoadingOverlay } from '@/components/feedback';
import { useTranslations } from '@/hooks';
import emptyBoxIllustration from '@/assets/illustrations/empty-box.png';
import { useGetInventoryItemQuery } from '../api/inventoryApi';
import { parseInventoryKey, isCompleteInventoryKey } from '../constants/inventoryKey';
import { INVENTORY_DETAIL_TABS } from '../constants/inventory.constants';
import { InventorySummaryTabView } from '../components/InventorySummaryTabView';
import { InventoryInboundsTabView } from '../components/InventoryInboundsTabView';
import { InventoryOutboundsTabView } from '../components/InventoryOutboundsTabView';
import { InventoryTransactionsTabView } from '../components/InventoryTransactionsTabView';
import styles from './InventoryDetailPage.module.scss';

// Monitor ▸ Inventory ▸ material details — Summary, Inbounds, Outbounds and Transactions.
export const InventoryDetailPage = () => {
  const t = useTranslations();
  const navigate = useNavigate();
  const { module, inventoryId } = useParams();
  const [activeTab, setActiveTab] = useState(INVENTORY_DETAIL_TABS.summary);

  const key = useMemo(() => parseInventoryKey(inventoryId), [inventoryId]);
  const isValidKey = isCompleteInventoryKey(key);

  const {
    data: detail,
    isLoading,
    isError,
  } = useGetInventoryItemQuery(
    {
      facilityType: key.facilityType,
      facilityId: key.facilityId,
      materialId: key.materialId,
      terminalId: key.terminalId ?? undefined,
    },
    { skip: !isValidKey },
  );

  const tabs = useMemo(
    () => [
      { value: INVENTORY_DETAIL_TABS.summary, label: t('SECTION_SUMMARY') },
      { value: INVENTORY_DETAIL_TABS.inbounds, label: t('NAV_INBOUNDS') },
      { value: INVENTORY_DETAIL_TABS.outbounds, label: t('NAV_OUTBOUNDS') },
      { value: INVENTORY_DETAIL_TABS.transactions, label: t('NAV_TRANSACTIONS') },
    ],
    [t],
  );

  const breadcrumbItems = useMemo(
    () => [
      { label: t('INVENTORY_TITLE'), onClick: () => navigate(ROUTES.inventory(module)) },
      { label: detail?.materialName ?? '' },
    ],
    [t, navigate, module, detail],
  );

  const metaFields = useMemo(
    () => [
      { id: 'materialId', label: t('FIELD_MATERIAL_ID'), value: detail?.materialId },
      {
        id: 'facility',
        label: t('FIELD_SITE_OR_B2B_ACCOUNT_NAME'),
        value: detail?.facilityName,
      },
      { id: 'terminal', label: t('FIELD_TERMINAL'), value: detail?.terminal },
      {
        id: 'availableDeclaration',
        label: t('FIELD_AVAILABLE_QUANTITY_IN_UNIT', { unit: detail?.declarationUnit ?? '' }),
        value: detail?.availableQuantityDeclaration,
      },
      {
        id: 'availableWorking',
        label: t('FIELD_AVAILABLE_QUANTITY_IN_UNIT', { unit: detail?.workingUnit ?? '' }),
        value: detail?.availableQuantityWorking,
      },
    ],
    [t, detail],
  );

  // The tabs address the row by its parts, not by the list row's id.
  const item = useMemo(
    () => ({
      facilityType: key.facilityType,
      facilityId: key.facilityId,
      materialId: key.materialId,
      terminalId: key.terminalId,
      terminal: detail?.terminal,
    }),
    [key, detail],
  );

  const certificateType = MODULE_CERTIFICATE_TYPES[module];
  const units = useMemo(
    () => ({ declaration: detail?.declarationUnit ?? '', working: detail?.workingUnit ?? '' }),
    [detail],
  );

  if (!isValidKey || isError || (!isLoading && !detail)) {
    return (
      <div className={styles.page}>
        <EmptyState
          image={<img src={emptyBoxIllustration} alt="" width={200} height={200} />}
          title={t('INVENTORY_DETAIL_ERROR_TITLE')}
          description={t('INVENTORY_DETAIL_ERROR_DESCRIPTION')}
        />
      </div>
    );
  }

  if (isLoading) return <LoadingOverlay label={t('COMMON_LOADING')} />;

  return (
    <div className={styles.page}>
      <DetailPageHeader
        breadcrumbItems={breadcrumbItems}
        title={detail?.materialName ?? ''}
        metaFields={metaFields}
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        tabsAriaLabel={t('INVENTORY_TITLE')}
        compactMeta
      />

      <div className={styles.content}>
        {activeTab === INVENTORY_DETAIL_TABS.summary && (
          <InventorySummaryTabView
            item={item}
            units={units}
            module={module}
            certificateType={certificateType}
          />
        )}

        {activeTab === INVENTORY_DETAIL_TABS.inbounds && (
          <InventoryInboundsTabView module={module} certificateType={certificateType} item={item} />
        )}

        {activeTab === INVENTORY_DETAIL_TABS.outbounds && (
          <InventoryOutboundsTabView
            module={module}
            certificateType={certificateType}
            item={item}
          />
        )}

        {activeTab === INVENTORY_DETAIL_TABS.transactions && (
          <InventoryTransactionsTabView
            module={module}
            certificateType={certificateType}
            item={item}
          />
        )}
      </div>
    </div>
  );
};
